import { PlusCircleFilled, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, Form, Grid, Input, Row, Table } from "antd";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AddBrand, GetAllBrandsPaginated } from "../../api/api";

const { useBreakpoint } = Grid;

const decodeHtml = (str = "") => {
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
};

const slugify = (str = "") =>
  str
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const Marcas = () => {
  const [form] = Form.useForm();
  const screens = useBreakpoint();
  const [brands, setBrands] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  // force=true ignora la caché (botón "Actualizar")
  const loadBrands = useCallback(async (force = false) => {
    setLoadingList(true);
    try {
      setBrands(await GetAllBrandsPaginated(force === true));
    } catch (error) {
      toast.error("Error al cargar las marcas: " + error.message);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  const findExisting = (name) => {
    const slug = slugify(name);
    return brands.find(
      (b) =>
        decodeHtml(b.name).trim().toLowerCase() === name.toLowerCase() ||
        b.slug === slug
    );
  };

  const onFinish = async (values) => {
    const name = values.name.trim();
    const existing = findExisting(name);
    if (existing) {
      toast.warning(`La marca "${decodeHtml(existing.name)}" ya existe`);
      return;
    }

    setSaving(true);
    const res = await AddBrand({ name, slug: slugify(name) });
    setSaving(false);
    if (res.ok && res.data?.id) {
      toast.success(`Marca "${decodeHtml(res.data.name)}" creada correctamente`);
      form.resetFields();
      loadBrands();
    } else if (res.data?.code === "term_exists") {
      toast.warning(`La marca "${name}" ya existe`);
      loadBrands();
    } else {
      toast.error(
        "Error al crear la marca: " + (res.data?.message || res.problem)
      );
    }
  };

  const filtered = brands.filter((b) =>
    decodeHtml(b.name).toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      sorter: (a, b) => decodeHtml(a.name).localeCompare(decodeHtml(b.name)),
      render: (name) => decodeHtml(name),
    },
    {
      title: "Slug",
      dataIndex: "slug",
      render: (slug) => <span style={{ color: "#8c8c8c" }}>{slug}</span>,
    },
    {
      title: "Productos",
      dataIndex: "count",
      align: "right",
      sorter: (a, b) => a.count - b.count,
    },
  ];

  // bordered={false}: evita el CSS global que fija el ancho de .ant-card-bordered
  const cardStyle = {
    width: "100%",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    background: "#fff",
  };

  // El menú lateral es fijo (200px) en pantallas > 800px
  const contentStyle = {
    marginLeft: screens.md ? 200 : 0,
    padding: "20px 16px",
  };

  return (
    <Row gutter={[16, 16]} style={contentStyle}>
      <Col xs={24} xl={8}>
        <Card title="Añadir nueva marca" bordered={false} style={cardStyle}>
          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              name="name"
              label="Nombre"
              validateTrigger="onChange"
              rules={[
                { required: true, whitespace: true, message: "Ingresa el nombre de la marca" },
                {
                  validator: (_, value) =>
                    value && value.trim() && findExisting(value.trim())
                      ? Promise.reject(new Error("Esta marca ya existe"))
                      : Promise.resolve(),
                },
              ]}
            >
              <Input placeholder="Ej: ADIDAS" autoFocus />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlusCircleFilled />}
              loading={saving}
              block
            >
              Añadir nueva marca
            </Button>
          </Form>
        </Card>
      </Col>
      <Col xs={24} xl={16}>
        <Card
          title={`Marcas creadas (${brands.length})`}
          bordered={false}
          style={cardStyle}
          extra={
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadBrands(true)}
              loading={loadingList}
            >
              Actualizar
            </Button>
          }
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              placeholder="Buscar marca por nombre"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: "1 1 240px", maxWidth: 380 }}
            />
            <span style={{ color: "#8c8c8c" }}>
              {search
                ? `${filtered.length} de ${brands.length} marcas`
                : `${brands.length} marcas`}
            </span>
          </div>
          <Table
            rowKey="id"
            size="small"
            loading={loadingList}
            columns={columns}
            dataSource={filtered}
            pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
            locale={{ emptyText: search ? "Ninguna marca coincide con la búsqueda" : "Aún no hay marcas" }}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default Marcas;
